import React, { useEffect, useState } from "react";
import { api, formatErr, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Users2, Plus, Pencil, Trash2, DollarSign, PlayCircle } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { companyCurrencies } from "@/lib/currencies";

const EMPTY = { name: "", role: "", phone: "", email: "", salary: 0, currency: "SYP", hire_date: "", notes: "", active: true };

export default function Payroll() {
  const { currentCompany } = useAuth();
  const { t } = useI18n();
  const ti = (key, vars = {}) => Object.entries(vars).reduce((s, [k, v]) => s.replaceAll(`{${k}}`, v), t(key));
  const [emps, setEmps] = useState([]);
  const [runs, setRuns] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [runOpen, setRunOpen] = useState(false);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [runItems, setRunItems] = useState([]);

  const load = async () => {
    try {
      const [e, r] = await Promise.all([api.get("/employees"), api.get("/payroll/runs")]);
      setEmps(e.data); setRuns(r.data);
    } catch (err) { toast.error(formatErr(err)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) return toast.error(t("hr_toast_emp_name_req"));
    try {
      if (editing) await api.put(`/employees/${editing.id}`, form);
      else await api.post("/employees", form);
      toast.success(t("saved")); setOpen(false); setEditing(null); setForm(EMPTY); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const del = async (e) => {
    if (!confirm(ti("hr_confirm_del_emp2", { name: e.name }))) return;
    try { await api.delete(`/employees/${e.id}`); toast.success(t("hr_toast_done")); load(); }
    catch (err) { toast.error(formatErr(err)); }
  };

  const openRun = () => {
    setRunItems(emps.filter(e => e.active).map(e => ({
      employee_id: e.id, employee_name: e.name, base_salary: e.salary || 0,
      bonus: 0, deduction: 0, currency: e.currency || "SYP",
    })));
    setRunOpen(true);
  };
  const runPayroll = async () => {
    try {
      const { data } = await api.post("/payroll/run", { month, items: runItems });
      toast.success(ti("hr_toast_paid", { n: data.items.length }));
      setRunOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-6" data-testid="payroll-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold flex items-center gap-2"><Users2 className="w-6 h-6 text-primary" /> {t("hr_payroll_title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("hr_payroll_sub")}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={openRun} className="gap-1 bg-emerald-600 hover:bg-emerald-700" data-testid="run-payroll-btn"><PlayCircle className="w-4 h-4" /> {t("hr_run_payroll")}</Button>
          <Button onClick={()=>{setEditing(null); setForm(EMPTY); setOpen(true);}} className="gap-1" data-testid="new-emp-btn"><Plus className="w-4 h-4" /> {t("hr_new_employee")}</Button>
        </div>
      </div>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="text-start">{t("name")}</TableHead>
            <TableHead className="text-start">{t("hr_role")}</TableHead>
            <TableHead className="text-start">{t("phone")}</TableHead>
            <TableHead className="text-start">{t("hr_salary")}</TableHead>
            <TableHead className="text-start">{t("hr_employment")}</TableHead>
            <TableHead className="text-start">{t("status")}</TableHead>
            <TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {emps.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">{t("hr_no_employees")}</TableCell></TableRow>}
            {emps.map(e => (
              <TableRow key={e.id} data-testid={`emp-${e.id}`}>
                <TableCell className="font-semibold">{e.name}</TableCell>
                <TableCell>{e.role || "—"}</TableCell>
                <TableCell className="text-xs">{e.phone || "—"}</TableCell>
                <TableCell className="tabular-nums font-bold">{fmtMoney(e.salary, e.currency)}</TableCell>
                <TableCell className="text-xs">{e.hire_date || "—"}</TableCell>
                <TableCell>{e.active ? <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">{t("active")}</span> : <span className="text-xs px-2 py-0.5 rounded bg-slate-200 text-slate-600">{t("hr_suspended")}</span>}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={()=>{setEditing(e); setForm({...e, hire_date: e.hire_date || ""}); setOpen(true);}} className="w-7 h-7"><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="icon" variant="ghost" onClick={()=>del(e)} className="w-7 h-7 text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {runs.length > 0 && (
        <div>
          <h2 className="text-lg font-heading font-bold mb-2 flex items-center gap-2"><DollarSign className="w-5 h-5 text-primary" /> {t("hr_monthly_log")}</h2>
          <Card className="overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead>{t("hr_month")}</TableHead>
                <TableHead>{t("hr_emp_count")}</TableHead>
                <TableHead>{t("total")}</TableHead>
                <TableHead>{t("hr_by")}</TableHead>
                <TableHead>{t("date")}</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {runs.map(r => (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono font-bold">{r.month}</TableCell>
                    <TableCell>{r.items.length}</TableCell>
                    <TableCell className="tabular-nums">{Object.entries(r.totals || {}).map(([c, v]) => `${v.toLocaleString("ar-EG")} ${c}`).join(" · ")}</TableCell>
                    <TableCell className="text-xs">{r.created_by_name}</TableCell>
                    <TableCell className="text-xs">{new Date(r.created_at).toLocaleDateString("ar-EG")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </div>
      )}

      {/* Employee dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="emp-dialog">
          <DialogHeader><DialogTitle>{editing ? t("hr_edit_emp") : t("hr_new_employee")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>{t("hr_name_req")}</Label><Input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="emp-name" /></div>
            <div><Label>{t("hr_role")}</Label><Input value={form.role} onChange={(e)=>setForm({...form, role: e.target.value})} placeholder={t("hr_role_ph2")} /></div>
            <div><Label>{t("phone")}</Label><Input value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} /></div>
            <div><Label>{t("hr_email")}</Label><Input type="email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} /></div>
            <div><Label>{t("hr_hire_date2")}</Label><Input type="date" value={form.hire_date} onChange={(e)=>setForm({...form, hire_date: e.target.value})} /></div>
            <div><Label>{t("hr_monthly_salary")}</Label><Input type="number" value={form.salary} onChange={(e)=>setForm({...form, salary: +e.target.value})} data-testid="emp-salary" /></div>
            <div>
              <Label>{t("currency")}</Label>
              <Select value={form.currency} onValueChange={(v)=>setForm({...form, currency: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(companyCurrencies(currentCompany)).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>{t("notes")}</Label><Input value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} /></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-emp-btn">{t("save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payroll run dialog */}
      <Dialog open={runOpen} onOpenChange={setRunOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PlayCircle className="w-5 h-5 text-emerald-600" /> {t("hr_run_month")}
              <Input type="month" value={month} onChange={(e)=>setMonth(e.target.value)} className="w-40 h-8 ms-2" />
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>{t("hr_employee")}</TableHead>
                <TableHead>{t("hr_base_salary")}</TableHead>
                <TableHead>{t("hr_bonus")}</TableHead>
                <TableHead>{t("hr_deduction")}</TableHead>
                <TableHead>{t("hr_net")}</TableHead>
                <TableHead>{t("currency")}</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {runItems.map((it, idx) => {
                  const net = (+it.base_salary || 0) + (+it.bonus || 0) - (+it.deduction || 0);
                  return (
                    <TableRow key={idx}>
                      <TableCell className="font-semibold">{it.employee_name}</TableCell>
                      <TableCell><Input type="number" value={it.base_salary} onChange={(e)=>{const n=[...runItems]; n[idx].base_salary=+e.target.value; setRunItems(n);}} className="h-8 w-28 text-sm" /></TableCell>
                      <TableCell><Input type="number" value={it.bonus} onChange={(e)=>{const n=[...runItems]; n[idx].bonus=+e.target.value; setRunItems(n);}} className="h-8 w-24 text-sm" /></TableCell>
                      <TableCell><Input type="number" value={it.deduction} onChange={(e)=>{const n=[...runItems]; n[idx].deduction=+e.target.value; setRunItems(n);}} className="h-8 w-24 text-sm" /></TableCell>
                      <TableCell className="tabular-nums font-bold text-emerald-700">{net.toLocaleString("ar-EG")}</TableCell>
                      <TableCell className="font-mono text-xs">{it.currency}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <div className="text-sm text-muted-foreground bg-amber-50 border border-amber-200 rounded p-2">
            {ti("hr_payroll_note", { month })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setRunOpen(false)}>{t("cancel")}</Button>
            <Button onClick={runPayroll} className="bg-emerald-600 hover:bg-emerald-700 gap-1" data-testid="confirm-payroll-btn"><PlayCircle className="w-4 h-4" /> {t("hr_pay_salaries")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
